/**
 * The sign-up wizard (F3).
 *
 * Four steps, in the order Matt described: which one are you · the other person
 * · your answers, confirmed · the price.
 *
 * IT SAVES ON EVERY STEP. Somebody filling in a form about their own
 * immigration case at eleven at night on a phone will close the tab, and coming
 * back to a blank form is how they do not come back at all. Partial state is
 * the normal state of a form, not an error.
 *
 * THE PRICE COMES FROM THE SERVER. Rendering it from a constant in this bundle
 * would mean a stale deploy could show one figure while the payment intent used
 * another — somebody agreeing to one number and being asked for a different
 * one. The tier arrives with the onboarding state and is displayed, never
 * computed.
 *
 * Every node is createElement + textContent (§3.8).
 */
import { el } from "./dom.js";

export interface Tier {
  id: string;
  name: string;
  amount: number;
  currency: string;
  summary: string;
  includes: string[];
  display: string;
}

export interface OnboardingState {
  onboarding: {
    role?: "applicant" | "sponsor";
    your_name?: string;
    partner_name?: string;
    partner_email?: string;
    tier_id?: string;
  };
  /** When the CASE was set up, by whoever did it first. */
  completed_at: string | null;
  /** When THIS person finished their own sign-up. Null means show them this. */
  mine_completed_at: string | null;
  role: "applicant" | "sponsor" | "adviser" | null;
  tiers: Tier[];
  default_tier_id: string;
}

type Patch = Record<string, unknown>;

export interface OnboardingDeps {
  save: (patch: Patch) => Promise<boolean>;
  /**
   * Begin payment. Resolves to a Stripe Checkout URL, or null when payment is
   * not switched on — which is the normal state until Matt turns it on, and is
   * why the wizard still finishes rather than trapping somebody on the last
   * step (F4).
   */
  startPayment: () => Promise<string | null>;
  invitePartner: (email: string) => Promise<boolean>;
  /** The determination behind this case, so step 3 can show what was checked. */
  caseSummary: () => { verdict: string; met: number; open: number } | null;
  onFinished: () => void;
}

const STEPS = ["role", "partner", "answers", "plan"] as const;
type Step = (typeof STEPS)[number];

const TITLES: Record<Step, { kicker: string; title: string }> = {
  role: { kicker: "Setting up", title: "Who are you?" },
  partner: { kicker: "Setting up", title: "Your partner's details" },
  answers: { kicker: "Setting up", title: "Requirements check" },
  plan: { kicker: "Setting up", title: "What Verity does, and what it costs" },
};

export function renderOnboarding(
  state: OnboardingState,
  deps: OnboardingDeps,
  container: HTMLElement,
): void {
  // THE SECOND PERSON JOINS A CASE THAT IS ALREADY SET UP. Role, partner and
  // plan were decided by whoever went first, so asking them again would be
  // asking them to re-decide their partner's answers. But showing them nothing
  // — which is what happened while completion was stamped on the household —
  // dropped them onto a dashboard having confirmed nothing and seen no price.
  if (state.completed_at && !state.mine_completed_at) {
    renderJoining(state, deps, container);
    return;
  }

  // Resume where they stopped rather than at the beginning. The saved answers
  // are the record of how far they got.
  const answered = state.onboarding;
  let step: Step =
    !answered.role
      ? "role"
      : !answered.partner_email
        ? "partner"
        : !answered.tier_id
          ? "answers"
          : "plan";

  const draft: Patch = { ...answered };

  function go(next: Step): void {
    step = next;
    paint();
  }

  /**
   * Back a step.
   *
   * Nothing is unsaved on the way back — every step already wrote — so this
   * only moves the cursor. The one step with a side effect is the invitation,
   * and returning to it and sending again WITHDRAWS the first: the server keeps
   * at most one live partner invitation per case, so correcting a mistyped
   * address cannot leave two working links, one of them to a stranger.
   */
  function back(): void {
    const index = STEPS.indexOf(step);
    if (index > 0) go(STEPS[index - 1]!);
  }

  async function saveAnd(patch: Patch, next: Step | "done"): Promise<void> {
    Object.assign(draft, patch);
    const ok = await deps.save(patch);
    if (!ok) {
      // Saving failed, so do not advance: pretending the answer was kept and
      // losing it two steps later is worse than saying so now.
      paint("That did not save. Check your connection and try again.");
      return;
    }
    if (next === "done") deps.onFinished();
    else go(next);
  }

  function paint(error?: string): void {
    container.replaceChildren();

    const head = TITLES[step];
    const header = el("div", "ob-head");

    // Only where there is somewhere to go. A disabled back button on step one
    // is a control that exists to be refused.
    if (STEPS.indexOf(step) > 0) {
      const backBtn = el("button", "ob-back", "Back");
      backBtn.type = "button";
      backBtn.addEventListener("click", back);
      header.append(backBtn);
    }

    header.append(el("p", "kicker", head.kicker));
    header.append(el("h1", undefined, head.title));
    header.append(progress(STEPS.indexOf(step)));
    container.append(header);

    if (error) container.append(el("p", "ob-error", error));

    const body = el("div", "ob-body");
    container.append(body);

    if (step === "role") renderRole(body);
    else if (step === "partner") renderPartner(body);
    else if (step === "answers") renderAnswers(body);
    else renderPlan(body);
  }

  function progress(index: number): HTMLElement {
    const wrap = el("div", "ob-progress");
    wrap.setAttribute("aria-label", `Step ${index + 1} of ${STEPS.length}`);
    STEPS.forEach((_, i) => {
      wrap.append(el("span", `ob-pip ${i <= index ? "done" : ""}`.trim()));
    });
    return wrap;
  }

  // ------------------------------------------------------------------ step 1

  function renderRole(body: HTMLElement): void {
    body.append(
      el(
        "p",
        "ob-lede",
        "The applicant is the person applying for the visa. The sponsor is their partner in the UK. Who you are decides what each of you is asked for.",
      ),
    );

    const choices = el("div", "ob-choices");
    for (const [role, label, note] of [
      ["applicant", "I am the applicant", "I am the one applying for the visa."],
      ["sponsor", "I am the sponsor", "I am the partner in the UK."],
    ] as const) {
      const button = el("button", `ob-choice ${draft.role === role ? "chosen" : ""}`.trim());
      button.type = "button";
      button.append(el("span", "ob-choice-label", label));
      button.append(el("span", "ob-choice-note", note));
      button.addEventListener("click", () => void saveAnd({ role }, "partner"));
      choices.append(button);
    }
    body.append(choices);
  }

  // ------------------------------------------------------------------ step 2

  function renderPartner(body: HTMLElement): void {
    const other = draft.role === "applicant" ? "sponsor" : "applicant";
    body.append(
      el(
        "p",
        "ob-lede",
        other === "sponsor"
          ? "Your partner in the UK is on this application too. We will send them an invitation so they can join the case."
          : "The applicant will need access too. We will send them an invitation so they can join it.",
      ),
    );

    const form = el("form", "ob-form");

    const yourName = field(form, "Your name", "text", String(draft.your_name ?? ""));
    const partnerName = field(form, "Their name", "text", String(draft.partner_name ?? ""));
    const partnerEmail = field(form, "Their email", "email", String(draft.partner_email ?? ""));

    // The account is created when THEY accept, never from what their partner
    // typed here (ADR-0045).
    form.append(
      el(
        "p",
        "ob-note",
        "They will be sent a link to create their own account and join the case.",
      ),
    );

    const resending = Boolean(answered.partner_email);
    if (resending) {
      form.append(
        el(
          "p",
          "ob-note",
          `We have already invited ${String(answered.partner_email)}. Sending again cancels that invitation and issues a new one, so only the newest link will work.`,
        ),
      );
    }

    const submit = el(
      "button",
      "btn btn-primary",
      resending ? "Send a new invitation" : "Send the invitation",
    );
    submit.type = "submit";
    form.append(submit);

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const email = partnerEmail.value.trim();
      if (!yourName.value.trim() || !partnerName.value.trim() || !email) {
        paint("Please fill in all three.");
        return;
      }
      void (async () => {
        const invited = await deps.invitePartner(email);
        if (!invited) {
          paint("That invitation did not send. Check the address and try again.");
          return;
        }
        await saveAnd(
          {
            your_name: yourName.value.trim(),
            partner_name: partnerName.value.trim(),
            partner_email: email,
          },
          "answers",
        );
      })();
    });

    body.append(form);
  }

  // ------------------------------------------------------------------ step 3

  function renderAnswers(body: HTMLElement): void {
    const summary = deps.caseSummary();

    if (!summary) {
      // Signed up without checking first — F6's path. The check is not a
      // detour here, it is the next thing to do.
      body.append(
        el(
          "p",
          "ob-lede",
          "We need to ask a few questions to understand your situation. The check takes a few minutes and your application depends on the answers, so it is the next thing to do.",
        ),
      );
      const link = el("a", "btn btn-primary", "Take the check");
      link.href = "/check/";
      body.append(link);
      return;
    }

    /**
     * NO SCOREBOARD HERE.
     *
     * This used to read "15 requirements met, 5 still open — does that look
     * right?", which asks somebody to validate a number they have no way to
     * check and did not produce. Worse, it arrives seconds after they finished
     * the check, so the honest answer to "does that look right" is "how would
     * I know".
     *
     * What they actually need at this step is confirmation that the check
     * landed, and a way on. The numbers belong on the dashboard, next to the
     * requirements they describe.
     */
    body.append(
      el(
        "p",
        "ob-lede",
        "Your answers are on your case. Everything Verity shows you is worked out from them, and you can change any of them later.",
      ),
    );

    const actions = el("div", "ob-actions");
    const confirm = el("button", "btn btn-primary", "Continue");
    confirm.type = "button";
    confirm.addEventListener("click", () => go("plan"));
    actions.append(confirm);

    const change = el("a", "btn", "Change an answer");
    change.href = "/check/";
    actions.append(change);
    body.append(actions);
  }

  // ------------------------------------------------------------------ step 4

  function renderPlan(body: HTMLElement): void {
    const tier = state.tiers.find((t) => t.id === state.default_tier_id) ?? state.tiers[0];
    if (!tier) {
      body.append(el("p", "ob-lede", "We could not load the plan. Please try again in a moment."));
      return;
    }

    const card = el("div", "ob-plan");
    const top = el("div", "ob-plan-top");
    top.append(el("div", "ob-plan-name", tier.name));
    // Displayed, never computed. The figure came from the server.
    top.append(el("div", "ob-plan-price", tier.display));
    card.append(top);
    card.append(el("p", "ob-plan-summary", tier.summary));

    const list = el("ul", "ob-plan-includes");
    for (const item of tier.includes) list.append(el("li", undefined, item));
    card.append(list);
    body.append(card);

    body.append(
      el(
        "p",
        "ob-note",
        "We ask for your card now and keep it on file. Nothing is charged and nothing is reserved — the payment is taken only once a registered adviser has been assigned to your case.",
      ),
    );

    const submit = el("button", "btn btn-primary", "Add your card");
    submit.type = "button";
    submit.addEventListener("click", () => {
      void (async () => {
        submit.disabled = true;
        // The wizard is finished either way. Completion means "we asked and
        // they answered" — payment is its own state, tracked separately, so
        // somebody who abandons at the card screen lands on a dashboard that
        // can tell them so rather than on the wizard forever (F4).
        Object.assign(draft, { tier_id: tier.id, completed: true });
        if (!(await deps.save({ tier_id: tier.id, completed: true }))) {
          submit.disabled = false;
          paint("That did not save. Check your connection and try again.");
          return;
        }

        const url = await deps.startPayment();
        // LEAVING THE ORIGIN. Card details never touch us — Stripe's hosted
        // page collects them, so nothing of theirs is loaded on our page and
        // no third-party script runs here (§3.8, prohibition 10).
        if (url) {
          window.location.assign(url);
          return;
        }
        // Payment is off, or Stripe refused. Finishing is still the right
        // outcome: the case is set up, and Billing carries what is outstanding.
        deps.onFinished();
      })();
    });
    body.append(submit);

    /**
     * SKIPPING IS ALLOWED, and says exactly what it costs.
     *
     * Somebody who has just been told the fee is £1,299 should not have to
     * produce a card before they can see their own case. Forcing it does not
     * produce more payments, it produces fewer completed sign-ups and a
     * conversion rate that is really a measurement of the wall.
     *
     * The two facts either side of the decision are stated plainly, because
     * both matter and they pull in opposite directions: nothing is taken until
     * an adviser is assigned, AND nothing progresses until the card is on file.
     */
    const later = el("button", "ob-skip", "I will add it later");
    later.type = "button";
    later.addEventListener("click", () => {
      void (async () => {
        later.disabled = true;
        if (!(await deps.save({ tier_id: tier.id, completed: true }))) {
          later.disabled = false;
          paint("That did not save. Check your connection and try again.");
          return;
        }
        deps.onFinished();
      })();
    });
    body.append(later);
    body.append(
      el(
        "p",
        "ob-note",
        "You can add your card from Billing at any time. Until it is there we cannot assign an adviser or start work on the application — but nothing is taken from it until an adviser has been assigned.",
      ),
    );
  }

  paint();
}

/**
 * What the SECOND person sees on their first sign-in.
 *
 * Not the four-step wizard. Their partner already chose the role, named them,
 * and picked the plan — asking again would be asking them to re-decide someone
 * else's answers, and the role in particular is theirs by invitation, not by
 * choice.
 *
 * But not nothing either, which is what they got while completion lived on the
 * household: they landed on a dashboard having confirmed nothing and never seen
 * the price. One screen, showing what was set up and what it costs, ending in
 * an acknowledgement that stamps THEIR completion.
 */
function renderJoining(
  state: OnboardingState,
  deps: OnboardingDeps,
  container: HTMLElement,
): void {
  container.replaceChildren();

  const answered = state.onboarding;
  const partnerName = answered.your_name;
  const you = state.role === "applicant" ? "the applicant" : "the sponsor";

  const header = el("div", "ob-head");
  header.append(el("p", "kicker", "Joining"));
  header.append(
    el("h1", undefined, partnerName ? `${partnerName} has started your application` : "Your application has been started"),
  );
  container.append(header);

  const body = el("div", "ob-body");
  container.append(body);

  body.append(
    el(
      "p",
      "ob-lede",
      `You are on this case as ${you}. That was set when you were invited, so there is nothing to choose here — this is what has been set up so far.`,
    ),
  );

  const tier = state.tiers.find((t) => t.id === answered.tier_id)
    ?? state.tiers.find((t) => t.id === state.default_tier_id)
    ?? state.tiers[0];

  if (tier) {
    const card = el("div", "ob-plan");
    const top = el("div", "ob-plan-top");
    top.append(el("div", "ob-plan-name", tier.name));
    // Displayed, never computed — the figure came from the server.
    top.append(el("div", "ob-plan-price", tier.display));
    card.append(top);
    card.append(el("p", "ob-plan-summary", tier.summary));
    const list = el("ul", "ob-plan-includes");
    for (const item of tier.includes) list.append(el("li", undefined, item));
    card.append(list);
    body.append(card);
  }

  body.append(
    el(
      "p",
      "ob-note",
      "You can see everything on this case, and everyone who has access to it, under Account.",
    ),
  );

  const submit = el("button", "btn btn-primary", "Got it — take me to the case");
  submit.type = "button";
  submit.addEventListener("click", () => {
    void (async () => {
      submit.disabled = true;
      // Stamps THIS person's completion. The household's was stamped by
      // whoever set the case up, and is not touched.
      const ok = await deps.save({ completed: true });
      if (!ok) {
        submit.disabled = false;
        body.append(el("p", "ob-error", "That did not save. Check your connection and try again."));
        return;
      }
      deps.onFinished();
    })();
  });
  body.append(submit);
}

function field(form: HTMLElement, label: string, type: string, value: string): HTMLInputElement {
  const wrap = el("label", "ob-field");
  wrap.append(el("span", "ob-field-label", label));
  const input = el("input");
  input.type = type;
  input.value = value;
  input.required = true;
  wrap.append(input);
  form.append(wrap);
  return input;
}
